import { Outlet, useRouterState } from "@tanstack/react-router";
import { TriangleAlert } from "lucide-react";

import { ago } from "@/lib/format";
import { useIsInternal } from "@/lib/lookups";
import { useNow } from "@/lib/now";
import { StreamProvider, useStreamState } from "@/stream/stream";
import { AlertDrawer } from "./AlertDrawer";
import { CommandPalette } from "./CommandPalette";
import { Inspector } from "./Inspector";
import { Rail } from "./Rail";
import { TopBar } from "./TopBar";

/** PRD §5.11: silence must never look like green. */
function StaleBanner() {
  const { state, lastAt } = useStreamState();
  const now = useNow();
  if (state !== "stale") return null;
  return (
    <div role="alert" className="flex items-center gap-2 border-b border-status-down/40 bg-status-down/15 px-4 py-2 text-sm text-status-down">
      <TriangleAlert className="size-4 shrink-0" />
      <span>
        Live updates stopped{lastAt ? ` (last update ${ago(new Date(lastAt).toISOString(), now)})` : ""}. Statuses on screen may be out of date. Reconnecting…
      </span>
    </div>
  );
}

export function AppShell() {
  const internal = useIsInternal();
  // Wall displays (PRD §5.2 ?mode=wall): no chrome, but the stale banner stays.
  const wall = useRouterState({ select: (s) => s.location.pathname === "/topology" && (s.location.search as { mode?: string }).mode === "wall" });
  if (wall) {
    return (
      <StreamProvider>
        <div className="relative h-dvh overflow-hidden">
          <Outlet />
          <div className="absolute inset-x-0 top-12 z-10"><StaleBanner /></div>
        </div>
      </StreamProvider>
    );
  }
  return (
    <StreamProvider>
      <div className="flex h-dvh overflow-hidden">
        <Rail />
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar />
          <StaleBanner />
          <main className="min-h-0 flex-1 overflow-y-auto">
            <Outlet />
          </main>
          <Inspector />
        </div>
        {/* Incidents are the NOC's workflow (docs/adr/2026-09-28-customer-tenancy.md). */}
        {internal && <AlertDrawer />}
      </div>
      <CommandPalette />
    </StreamProvider>
  );
}
